/**
 * MentionPicker — autocomplete popover for `@` mentions in the
 * conversation composer (iter136).
 *
 * Triggered by the parent textarea when the user types ``@`` and the
 * cursor is immediately after a word boundary. The parent owns the
 * textarea + caret position; this component is purely presentational
 * + keyboard navigation. On selection it calls ``onSelect(token)``
 * with the canonical inline marker, e.g. ``@[Gerry Gualtieri](person:11012)``.
 *
 * Renders three groups:
 *   - Special: ``@everyone`` and ``@here``
 *   - Persons in the conversation's PCS
 *   - Staff in the conversation's PCS (UsAir team)
 */
import { memo, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Users, Lightning, At } from "@phosphor-icons/react";

import api from "@/lib/api";
import { useT } from "@/lib/i18n";

const SPECIAL = [
  { kind: "everyone", icon: Users },
  { kind: "here", icon: Lightning },
];

function MentionPickerImpl({
  conversationId,
  query,           // current text after the trigger ``@``
  onSelect,        // (token: string, label: string) => void
  onClose,         // () => void — caller dismisses
  anchor,          // {top, left} — viewport coords of the desired top-left of the popover
}) {
  const t = useT();
  const [items, setItems] = useState([]);
  const [activeIdx, setActiveIdx] = useState(0);
  const listRef = useRef(null);
  const reqIdRef = useRef(0);

  // ── Fetch candidates whenever the typed query changes (120 ms debounce).
  useEffect(() => {
    if (!conversationId) return;
    const myReq = ++reqIdRef.current;
    const handle = setTimeout(() => {
      api.get(
        `/conv/conversations/${conversationId}/mention-candidates`,
        { params: { q: query || "", limit: 25 } },
      )
        .then((r) => {
          if (myReq !== reqIdRef.current) return;
          setItems(Array.isArray(r.data?.items) ? r.data.items : []);
        })
        .catch(() => {
          if (myReq !== reqIdRef.current) return;
          setItems([]);
        });
    }, 120);
    return () => clearTimeout(handle);
  }, [conversationId, query]);

  // ── Build the merged option list (specials filtered by query too).
  const options = useMemo(() => {
    const q = (query || "").trim().toLowerCase();
    const showSpecials = SPECIAL.filter((s) => {
      if (!q) return true;
      return s.kind.startsWith(q);
    }).map((s) => ({
      type: "special",
      kind: s.kind,
      icon: s.icon,
      label: t(`convv2.mention_${s.kind}_label`),
      subtitle: t(`convv2.mention_${s.kind}_subtitle`),
      token: `@[${s.kind}](${s.kind}:*)`,
    }));
    const persons = items.map((u) => ({
      type: "user",
      key: `${u.user_type}:${u.user_id}`,
      user_type: u.user_type,
      user_id: u.user_id,
      label: u.name || `${u.user_type}:${u.user_id}`,
      subtitle: u.company_name || "",
      token: `@[${u.name || u.user_type + ":" + u.user_id}](${u.user_type}:${u.user_id})`,
    }));
    return [...showSpecials, ...persons];
  }, [items, query, t]);

  // Reset highlight when option list shrinks/changes.
  useEffect(() => {
    if (activeIdx >= options.length) setActiveIdx(0);
  }, [options.length, activeIdx]);

  // Keep the highlighted row in view inside the scroller.
  useEffect(() => {
    const el = listRef.current?.querySelector(
      `[data-mention-idx="${activeIdx}"]`,
    );
    if (el && el.scrollIntoView) {
      el.scrollIntoView({ block: "nearest" });
    }
  }, [activeIdx]);

  const commitAt = useCallback((idx) => {
    const opt = options[idx];
    if (!opt) return;
    onSelect(opt.token, opt.label);
  }, [options, onSelect]);

  // ── Public keyboard API: parent forwards keydown events.
  // The textarea retains focus; we hook listeners on `window`.
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "ArrowDown") {
        e.preventDefault();
        setActiveIdx((i) => (options.length ? (i + 1) % options.length : 0));
      } else if (e.key === "ArrowUp") {
        e.preventDefault();
        setActiveIdx((i) => (options.length ? (i - 1 + options.length) % options.length : 0));
      } else if (e.key === "Enter" || e.key === "Tab") {
        if (options.length === 0) return;
        e.preventDefault();
        e.stopPropagation();
        commitAt(activeIdx);
      } else if (e.key === "Escape") {
        e.preventDefault();
        onClose();
      }
    };
    window.addEventListener("keydown", onKey, true);
    return () => window.removeEventListener("keydown", onKey, true);
  }, [activeIdx, commitAt, onClose, options.length]);

  // ── Position: pinned above the caret so it doesn't cover what
  //    you're typing. Caller passes raw viewport coords.
  const style = {
    position: "fixed",
    left: Math.max(8, Math.min(anchor.left, window.innerWidth - 320)),
    bottom: Math.max(8, window.innerHeight - anchor.top + 8),
    width: 300,
    maxHeight: 280,
    zIndex: 70,
  };

  return (
    <div
      ref={listRef}
      data-testid="conv-mention-picker"
      className="rounded-md border border-slate-200 bg-white shadow-lg overflow-hidden flex flex-col"
      style={style}
      onMouseDown={(e) => {
        // Prevent the textarea from losing focus when clicking a row.
        e.preventDefault();
      }}
    >
      <div className="px-2.5 py-1.5 text-[10px] font-bold uppercase tracking-wider text-slate-500 border-b border-slate-100 flex items-center gap-1.5">
        <At size={11} weight="bold" />
        <span>{t("convv2.mention_picker_hint")}</span>
      </div>
      <div className="flex-1 overflow-y-auto" style={{ maxHeight: 220 }}>
        {options.length === 0 ? (
          <div className="px-3 py-3 text-[12px] text-slate-400 italic">
            {t("convv2.mention_no_matches")}
          </div>
        ) : (
          options.map((opt, idx) => {
            const isActive = idx === activeIdx;
            return (
              <button
                key={opt.type === "special" ? `s:${opt.kind}` : opt.key}
                data-mention-idx={idx}
                data-testid={`conv-mention-option-${
                  opt.type === "special" ? opt.kind : opt.key
                }`}
                onMouseEnter={() => setActiveIdx(idx)}
                onClick={() => commitAt(idx)}
                className={`w-full flex items-center gap-2 px-3 py-2 text-left transition-colors ${
                  isActive ? "bg-emerald-50" : "hover:bg-slate-50"
                }`}
              >
                {opt.type === "special" ? (
                  <span
                    className="flex items-center justify-center w-7 h-7 rounded-full text-white shrink-0"
                    style={{ background: opt.kind === "everyone" ? "#0ea5e9" : "#f59e0b" }}
                  >
                    <opt.icon size={13} weight="bold" />
                  </span>
                ) : (
                  <span
                    className="flex items-center justify-center w-7 h-7 rounded-full text-white text-[10px] font-bold shrink-0"
                    style={{ background: avatarColor(opt.label) }}
                  >
                    {initials(opt.label)}
                  </span>
                )}
                <span className="flex-1 min-w-0">
                  <span className="block text-[13px] text-slate-900 font-medium truncate">
                    {opt.type === "special" ? `@${opt.kind}` : opt.label}
                  </span>
                  {opt.subtitle && (
                    <span className="block text-[11px] text-slate-500 truncate">
                      {opt.subtitle}
                    </span>
                  )}
                </span>
              </button>
            );
          })
        )}
      </div>
    </div>
  );
}

export const MentionPicker = memo(MentionPickerImpl);

// Tiny helpers — kept inline so the component is self-contained.
function initials(name) {
  if (!name) return "?";
  const parts = String(name).trim().split(/\s+/).slice(0, 2);
  return parts.map((p) => p[0]?.toUpperCase() || "").join("") || "?";
}

const PALETTE = [
  "#0ea5e9", "#6366f1", "#a855f7", "#ec4899", "#f43f5e",
  "#f97316", "#eab308", "#22c55e", "#14b8a6", "#0ea5e9",
];
function avatarColor(seed) {
  if (!seed) return PALETTE[0];
  let h = 0;
  for (let i = 0; i < seed.length; i += 1) h = (h * 31 + seed.charCodeAt(i)) >>> 0;
  return PALETTE[h % PALETTE.length];
}
